import type { CSSProperties } from "react";

import classNames from "classnames/bind";
import Image from "common/blocks/Image";
import SmartLink from "common/wrapper/SmartLink";
import useMediaQuery from "hooks/useMediaQuery/useMediaQuery";
import type { ResolvedAddedProtectionItem } from "resolvers/data/addedProtectionItemsResolver";
import type { ResolvedAddedProtectionSection } from "resolvers/data/addedProtectionSectionResolver";

import styles from "./AddedProtection.module.scss";

const cx = classNames.bind(styles);

const buildTabletInlineStyles = (count: number): CSSProperties =>
    count > 0
        ? {
              gridTemplateColumns: `repeat(${count}, 1fr)`,
          }
        : {};

export type AddedProtectionProps = Omit<ResolvedAddedProtectionSection, "codename" | "type">;

const AddedProtection = (props: AddedProtectionProps) => {
    const { isAboveTabletLarge } = useMediaQuery();
    const { sectionTitle, items } = props;
    const itemsLength = items.length;
    if (itemsLength === 0) {
        return null;
    }

    return (
        <div className={cx("addedProtection")}>
            <span className={cx("addedProtectionSectionTitle")}>{sectionTitle}</span>
            <div
                className={cx("addedProtectionItems")}
                style={isAboveTabletLarge ? buildTabletInlineStyles(itemsLength) : {}}
            >
                {items.map((item: ResolvedAddedProtectionItem) => {
                    const { link, description, image, id, title } = item;
                    const target =
                        !link.target || link.target.trim() === "" ? "_self" : link.target;

                    return (
                        <div className={cx("addedProtectionItem")} key={id}>
                            <div className={cx("addedProtectionItemImage")}>
                                <Image {...image} alt={image.alt} layout="fill" objectFit="cover" />
                            </div>
                            <span className={cx("addedProtectionItemTitle")}>{title}</span>
                            <span className={cx("addedProtectionItemDescription")}>
                                {description}
                            </span>
                            <div className={cx("addedProtectionItemLinkWrapper")}>
                                <SmartLink href={link.path}>
                                    <a target={target} className={cx("addedProtectionItemLink")}>
                                        {link.title}
                                    </a>
                                </SmartLink>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AddedProtection;
